/**
 * I18N / TRANSLATION CONTEXT
 * ─────────────────────────────────────────────────────────────
 * To add a new language:
 *   1. Create /locales/{lang}/ folder
 *   2. Copy all JSON files from /locales/en/ and translate values
 *   3. Add lang entry to SUPPORTED_LANGUAGES below
 *   4. Import files and add to TRANSLATIONS map
 *   → No other code changes needed anywhere in the app.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

import enCommon from '../locales/en/common.json';
import enHome   from '../locales/en/home.json';
import enAuth   from '../locales/en/auth.json';
import enCerts  from '../locales/en/certifications.json';
import enDash   from '../locales/en/dashboard.json';
import msCommon from '../locales/ms/common.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',        flag: '🇬🇧', dir: 'ltr' },
  // { code: 'ms', label: 'Bahasa Melayu', flag: '🇲🇾', dir: 'ltr' }, // uncomment when translated
  // { code: 'ta', label: 'தமிழ்',         flag: '🇮🇳', dir: 'ltr' },
  // { code: 'zh', label: '中文',           flag: '🇨🇳', dir: 'ltr' },
];

const TRANSLATIONS = {
  en: { common: enCommon, home: enHome, auth: enAuth, certifications: enCerts, dashboard: enDash },
  ms: { common: msCommon },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('db_lang') || 'en'; } catch { return 'en'; }
  });

  const changeLang = useCallback((code) => {
    setLang(code);
    try { localStorage.setItem('db_lang', code); } catch {}
    document.documentElement.lang = code;
    const info = SUPPORTED_LANGUAGES.find(l => l.code === code);
    if (info) document.documentElement.dir = info.dir;
  }, []);

  /** t(key, namespace?, vars?) — dot-notation key lookup with interpolation */
  const t = useCallback((key, namespace = 'common', vars = {}) => {
    const resolve = (lng) => {
      const ns = TRANSLATIONS[lng]?.[namespace];
      if (!ns) return undefined;
      return key.split('.').reduce((o, k) => o?.[k], ns);
    };
    let value = resolve(lang) ?? resolve('en');
    if (typeof value !== 'string') return key;
    return value.replace(/\{\{(\w+)\}\}/g, (_, k) =>
      vars[k] !== undefined ? String(vars[k]) : `{{${k}}}`
    );
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, changeLang, t, supportedLanguages: SUPPORTED_LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be inside I18nProvider');
  return ctx;
}
